import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import useBackend from '@contexts/useBackend/useBackend';
import useAuth from '@contexts/useAuth/useAuth';
import type { Language, InventoryListPreferencesType } from '@bett3r-dev/teselly-domain';
import { useTranslation } from 'react-i18next';

type Props = { children: ReactNode };

export type UserPreferencesType = {
  language?: Language;
  inventory?: InventoryListPreferencesType;
  billingEntityWizard?: { status: 'completed' | 'dismissed' };
  /**
   * Which saved report this user's Dashboard shows (2026-08-17).
   *
   * A POINTER on the user, not a flag on the report — see the domain schema for
   * why. `undefined` means "no choice", which the Dashboard renders as the curated
   * default; an id whose report has since been deleted resolves the same way,
   * because a pointer into another subdomain cannot be kept referentially honest.
   */
  dashboardLayoutId?: string;
};

export type UserPreferencesContextType = {
  preferences: UserPreferencesType | null;
  isLoading: boolean;
  updateLanguage: ( language: Language ) => Promise<void>;
  updateInventoryPreferences: ( inventory: InventoryListPreferencesType ) => Promise<void>;
  dismissBillingEntityWizard: () => Promise<void>;
  reopenBillingEntityWizard: () => Promise<void>;
  /** Point the Dashboard at `layoutId`, or `null` to go back to the default. */
  setDashboardReport: ( layoutId: string | null ) => Promise<void>;
};

export const userPreferencesContext = createContext<UserPreferencesContextType | null>( null );

export function ProvideUserPreferences({ children }: Props ) {
  const userPreferences = useProvideUserPreferences();
  return <userPreferencesContext.Provider value={userPreferences}>{children}</userPreferencesContext.Provider>;
}

export function useUserPreferences() {
  return useContext( userPreferencesContext );
}

export default useUserPreferences;

const INVENTORY_PREFS_KEY = 'teselly:inventory-preferences';

function loadCachedInventoryPreferences(): InventoryListPreferencesType | undefined {
  try {
    const raw = localStorage.getItem( INVENTORY_PREFS_KEY );
    return raw ? JSON.parse( raw ) : undefined;
  } catch {
    return undefined;
  }
}

function cacheInventoryPreferences( inventory: InventoryListPreferencesType ) {
  try {
    localStorage.setItem( INVENTORY_PREFS_KEY, JSON.stringify( inventory ));
  } catch { /* ignore storage errors */ }
}

function useProvideUserPreferences(): UserPreferencesContextType {
  const { backend } = useBackend() || {};
  const auth = useAuth();
  const user = auth?.user;
  const { i18n } = useTranslation();
  const [ preferences, setPreferences ] = useState<UserPreferencesType | null>(() => {
    const cachedInventory = loadCachedInventoryPreferences();
    return cachedInventory ? { inventory: cachedInventory } : null;
  });
  const [ isLoading, setIsLoading ] = useState( true );

  useEffect(() => {
    if ( !backend || !user?.id ) {
      setIsLoading( false );
      return;
    }
    const loadPreferences = async () => {
      try {
        setIsLoading( true );
        const cachedInventory = loadCachedInventoryPreferences();

        const [ cmResponse, ipResponse ] = await Promise.all([
          backend.queries.UserPreferencesById( user.id ).catch(() => null ),
          backend.queries.ItemsPublishingUserPreferencesReadmodelById( user.id ).catch(() => null )
        ]);

        const inventoryPrefs = ipResponse?.inventory?.list || cachedInventory;

        const merged: UserPreferencesType = {
          language: cmResponse?.language,
          inventory: inventoryPrefs,
          billingEntityWizard: cmResponse?.billingEntityWizard,
          dashboardLayoutId: cmResponse?.dashboardLayoutId
        };

        setPreferences( merged );

        if ( cmResponse?.language ) {
          await i18n.changeLanguage( cmResponse.language );
        }
      } catch ( error ) {
        console.warn( 'Failed to load user preferences:', error );
      } finally {
        setIsLoading( false );
      }
    };

    loadPreferences();
  }, [ backend, user?.id ]);

  const updateLanguage = async ( language: Language ) => {
    setPreferences( prev => ({ ...prev, language }));
    await i18n.changeLanguage( language );
    if ( !backend || !user?.id ) return;
    try {
      await backend.commands.ChangeUserLanguage({ id: user.id, body: { language }});
    } catch ( error ) {
      console.error( 'Failed to update language:', error );
      throw error;
    }
  };

  const updateInventoryPreferences = async ( inventory: InventoryListPreferencesType ) => {
    setPreferences( prev => ({ ...prev, inventory }));
    cacheInventoryPreferences( inventory );
    if ( !backend || !user?.id ) return;
    try {
      await backend.commands.ChangeInventoryListPreferences({ id: user.id, body: { list: inventory }});
    } catch ( error ) {
      console.error( 'Failed to update inventory preferences:', error );
      throw error;
    }
  };

  const dismissBillingEntityWizard = async () => {
    setPreferences( prev => ({ ...prev, billingEntityWizard: { status: 'dismissed' as const }}));
    if ( !backend || !user?.id ) return;
    // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- optimistic UI-preference toggle; console.error is an operator log for a non-critical preference (TV1-2010 waiver, not a rethrow).
    try {
      await backend.commands.DismissBillingEntityWizard({ id: user.id, body: {}});
    } catch ( error ) {
      console.error( 'Failed to dismiss billing entity wizard:', error );
    }
  };

  const reopenBillingEntityWizard = async () => {
    setPreferences( prev => {
      const { billingEntityWizard: _, ...rest } = prev ?? {};
      return rest;
    });
    if ( !backend || !user?.id ) return;
    // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- optimistic UI-preference toggle; console.error is an operator log for a non-critical preference (TV1-2010 waiver, not a rethrow).
    try {
      await backend.commands.ReopenBillingEntityWizard({ id: user.id, body: {}});
    } catch ( error ) {
      console.error( 'Failed to reopen billing entity wizard:', error );
    }
  };

  /**
   * Optimistic, and deliberately so: the readmodel round-trips through the
   * outbox, so waiting for it would leave the menu item you just clicked looking
   * unchanged for a beat. On failure the local value is rolled back — the
   * alternative (leaving it) shows a dashboard choice the server never accepted.
   */
  const setDashboardReport = async ( layoutId: string | null ) => {
    if ( !backend || !user?.id ) return;
    const previous = preferences?.dashboardLayoutId;
    setPreferences(( current ) => ({ ...( current ?? {}), dashboardLayoutId: layoutId ?? undefined }));
    try {
      await backend.commands.ChangeDashboardReport({
        id: user.id,
        body: { dashboardLayoutId: layoutId }
      });
    } catch ( error ) {
      setPreferences(( current ) => ({ ...( current ?? {}), dashboardLayoutId: previous }));
      throw error;
    }
  };

  return {
    preferences,
    isLoading,
    setDashboardReport,
    updateLanguage,
    updateInventoryPreferences,
    dismissBillingEntityWizard,
    reopenBillingEntityWizard
  };
}
